// [2026-09-02] ADDED — the paginated, searchable table every staff feed uses.
//
// A LOCAL COPY, NOT AN IMPORT FROM THE CLIENT PACKAGE, and that is deliberate. The customer app has
// a component of the same shape, but it is not part of that package's exported surface
// (src/index.js) — reaching into `…/src/views/platform/DataTable.jsx` would make this app depend on
// an internal path of another repository that is free to move it. The two also answer to different
// APIs: this one pages over /admin/v1 feeds that span every tenant.
//
// SERVER-SIDE PAGING AND SEARCH. Rows and the total both come from the API for the CURRENT page.
// Filtering an array already in the browser can only search what was downloaded, which for a
// cross-tenant TTL feed is a small and arbitrary slice of the truth.
//
// SEARCH IS DEBOUNCED (400ms) — typing an eight-character client id is eight queries otherwise,
// seven of them stale before they land.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Empty, Notice, Skeleton, downloadCsv } from '@incruiter/proctoring-client-frontend'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@incruiter/proctoring-client-frontend/src/components/ui/table.jsx'

function useDebounced(value, delay = 400) {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return settled
}

export default function DataTable({
  title,
  subtitle,
  columns,                 // [{ key, label, render?, align? }]
  fetchPage,               // ({ limit, offset, search, ...filters }) -> { rows, total }
  pageSize = 25,
  searchPlaceholder = 'Search…',
  filters = [],            // [{ key, label, options: [{ value, label }] }]
  onRowClick,
  rowKey = (row, i) => row.id ?? i,
  emptyTitle = 'Nothing here yet',
  emptyHint = '',
  csvName,
  refreshToken,            // change it to force a reload (after a write elsewhere on the screen)
  actions,                 // extra buttons in the header
}) {
  const [rawSearch, setRawSearch] = useState('')
  const search = useDebounced(rawSearch)
  const [filterValues, setFilterValues] = useState({})
  const [offset, setOffset] = useState(0)
  const [state, setState] = useState({ rows: [], total: 0, loading: true, error: null })

  // A NEW SEARCH OR FILTER MUST RESET TO PAGE 1 — otherwise narrowing 400 rows to 3 while sitting on
  // page 5 shows an empty table, which reads as "no results" when there are three.
  const filterSignature = JSON.stringify(filterValues)
  useEffect(() => { setOffset(0) }, [search, filterSignature])

  // Stops a slow response for an OLD query landing after a fast one for the new query and repainting
  // the table with rows that no longer match what the search box says.
  const requestId = useRef(0)

  useEffect(() => {
    const mine = ++requestId.current
    let live = true
    setState((s) => ({ ...s, loading: true, error: null }))
    fetchPage({ limit: pageSize, offset, search: search || undefined, ...filterValues })
      .then((data) => {
        if (!live || mine !== requestId.current) return
        setState({ rows: data.rows || [], total: data.total ?? (data.rows || []).length,
                   loading: false, error: null })
      })
      .catch((err) => {
        if (!live || mine !== requestId.current) return
        setState({ rows: [], total: 0, loading: false, error: err })
      })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, search, filterSignature, pageSize, refreshToken])

  const { rows, total, loading, error } = state
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + rows.length, total)
  const page = Math.floor(offset / pageSize) + 1
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const activeFilters = useMemo(
    () => Object.values(filterValues).filter(Boolean).length, [filterValues])

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)', padding: '18px 20px' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap',
                    justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 600 }}>{title}</div>
          {subtitle && <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{subtitle}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {actions}
          <input value={rawSearch} onChange={(e) => setRawSearch(e.target.value)}
                 placeholder={searchPlaceholder} style={{ minWidth: 210 }} aria-label="Search" />
          {filters.map((f) => (
            <select key={f.key} aria-label={f.label} value={filterValues[f.key] || ''}
                    onChange={(e) => setFilterValues((v) => ({ ...v, [f.key]: e.target.value || undefined }))}
                    style={{ padding: '9px 10px', borderRadius: 'var(--radius-sm)',
                             border: '1px solid var(--border)', background: 'var(--surface)',
                             color: 'inherit', font: 'inherit' }}>
              <option value="">{f.label}: all</option>
              {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ))}
          {(rawSearch || activeFilters > 0) && (
            <button className="btn ghost" onClick={() => { setRawSearch(''); setFilterValues({}) }}>
              Clear
            </button>
          )}
          {csvName && (
            // Exports THE CURRENT PAGE and says so. An "Export" that silently hands over 25 of 4,000
            // rows is worse than none, because the file looks complete.
            <button className="btn ghost" disabled={!rows.length}
                    onClick={() => downloadCsv(`${csvName}-page-${page}.csv`, rows)}>Export page</button>
          )}
        </div>
      </div>

      {error && <Notice kind="error">{error.message}</Notice>}

      {loading ? <Skeleton height={200} />
        : rows.length === 0 ? (
          <Empty title={emptyTitle}
                 hint={(rawSearch || activeFilters) ? 'No rows match this search or filter.' : emptyHint} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c) => (
                    <TableHead key={c.key}
                               style={c.align === 'right' ? { textAlign: 'right' } : undefined}>
                      {c.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={rowKey(row, i)}
                            onClick={onRowClick ? () => onRowClick(row) : undefined}
                            style={onRowClick ? { cursor: 'pointer' } : undefined}>
                    {columns.map((c) => (
                      <TableCell key={c.key}
                                 style={c.align === 'right' ? { textAlign: 'right' } : undefined}>
                        {c.render ? c.render(row) : (row[c.key] ?? '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginTop: 14, gap: 12, flexWrap: 'wrap' }}>
        <div className="muted" style={{ fontSize: 12.5 }}>
          {total === 0 ? 'No rows' : `Showing ${from}–${to} of ${total}`}
          {pages > 1 && ` · page ${page} of ${pages}`}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn ghost" disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - pageSize))}>← Previous</button>
          <button className="btn ghost" disabled={offset + pageSize >= total || loading}
                  onClick={() => setOffset(offset + pageSize)}>Next →</button>
        </div>
      </div>
    </div>
  )
}
